import React, { useState, useEffect } from "react";
import { HashLink } from "react-router-hash-link";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Cart() {
  const [cartItems, setCartItems] = useState(() => {
    return JSON.parse(localStorage.getItem("cart")) || [];
  });

  useEffect(() => {
    sessionStorage.removeItem("buyNowItem");
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
    window.dispatchEvent(new Event("cartUpdated"));
  }, [cartItems]);

  const updateQuantity = (index, delta) => {
    setCartItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item,
      ),
    );
  };

  const removeItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.unitPrice * item.quantity,
    0,
  );

  const totalItems = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const tax = Math.round(subtotal * 0.16);
  const freeDeliveryThreshold = 3000;

  const delivery =
    subtotal >= freeDeliveryThreshold && subtotal > 0
      ? 0
      : subtotal > 0
        ? 200
        : 0;

  const grandTotal = subtotal + tax + delivery;

  const progress = Math.min((subtotal / freeDeliveryThreshold) * 100, 100);

  const remaining = freeDeliveryThreshold - subtotal;

  // ================= ANIMATIONS =================

  const pageFade = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.4 },
    },
  };

  const listContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      x: 40,
      transition: { duration: 0.25 },
    },
  };

  return (
    <motion.div
      className="bg-white min-h-screen px-[6%] lg:px-[10%] py-20"
      variants={pageFade}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-6xl mx-auto">
        {cartItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-32 text-center space-y-6"
          >
            <div className="text-5xl">🍕</div>

            <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
              Your cart feels lonely
            </h2>

            <p className="text-sm text-gray-400 max-w-sm leading-relaxed">
              You haven’t added anything yet. Start building your perfect meal.
            </p>

            <HashLink
              smooth
              to="/menu"
              className="bg-red-600 text-white px-6 py-3 rounded-lg text-sm hover:bg-red-700 transition"
            >
              Browse Menu
            </HashLink>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-3 gap-16 mt-12">
            {/* LEFT SIDE ITEMS */}

            <motion.div
              className="md:col-span-2 space-y-10"
              variants={listContainer}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence>
                {cartItems.map((item, index) => (
                  <motion.div
                    key={item._id + item.size}
                    variants={itemAnimation}
                    exit="exit"
                    layout
                    className="flex gap-6 border-b border-gray-100 pb-8"
                  >
                    <div className="w-24 h-24 bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="object-contain max-h-20"
                      />
                    </div>

                    <div className="flex-1 flex justify-between">
                      <div className="space-y-2">
                        <h2 className="text-sm lg:text-base font-medium text-neutral-900 capitalize tracking-snug">
                          {item.name}
                        </h2>

                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                          {item.size || "Standard"}
                        </p>

                        <p className="text-xs lg:text-sm text-neutral-900 font-medium">
                          Rs {item.unitPrice.toLocaleString("en-PK")}
                        </p>

                        <button
                          onClick={() => removeItem(index)}
                          className="text-xs text-gray-400 hover:text-neutral-900 transition"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(index, -1)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-sm hover:bg-gray-100 transition"
                        >
                          –
                        </button>

                        <span className="w-6 text-center text-sm text-neutral-900">
                          {item.quantity}
                        </span>

                        <button
                          onClick={() => updateQuantity(index, 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-sm hover:bg-gray-100 transition"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* RIGHT SUMMARY */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-10 rounded-3xl border border-gray-100 h-max sticky top-24 flex flex-col"
            >
              <h2 className="text-xl font-medium text-neutral-900 tracking-tight mb-8">
                Order Summary
              </h2>

              <div className="space-y-5 text-sm">
                {/* PRODUCTS */}
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div
                      key={item._id + item.size}
                      className="flex items-center justify-between gap-4"
                    >
                      {/* Product info */}
                      <div className="flex items-center gap-1 flex-1 min-w-0 text-gray-500">
                        <span className="font-medium text-neutral-900 shrink-0">
                          {item.quantity}×
                        </span>

                        <span className="truncate">{item.name}</span>

                        {item.size && (
                          <span className="uppercase text-gray-400 text-[11px] shrink-0">
                            ({item.size})
                          </span>
                        )}
                      </div>

                      {/* Price */}
                      <span className="text-neutral-900 font-medium text-xs shrink-0">
                        Rs{" "}
                        {(item.unitPrice * item.quantity).toLocaleString(
                          "en-PK",
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {/* TOTALS */}
                <div className="border-t border-gray-100 pt-6 space-y-3 text-sm text-gray-500">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <span className="text-neutral-900">
                      Rs {subtotal.toLocaleString("en-PK")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span>Tax (16%)</span>
                    <span className="text-neutral-900">
                      Rs {tax.toLocaleString("en-PK")}
                    </span>
                  </div>

                  {/* FREE DELIVERY PROGRESS */}
                  {subtotal > 0 && subtotal < freeDeliveryThreshold && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-400">
                        Add Rs {remaining.toLocaleString("en-PK")} more for free
                        delivery
                      </p>

                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-red-600"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.6 }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span>Delivery</span>
                    <span className="text-neutral-900">
                      Rs {delivery.toLocaleString("en-PK")}
                    </span>
                  </div>

                  {/* TOTAL */}
                  <div className="flex items-center justify-between text-lg font-semibold text-neutral-900 pt-5 border-t border-gray-100">
                    <span>Total</span>
                    <span>Rs {grandTotal.toLocaleString("en-PK")}</span>
                  </div>
                </div>
              </div>

              {/* CHECKOUT BUTTON */}
              <div className="mt-10">
                <Link
                  to="/checkout"
                  state={{
                    fromCart: true,
                    cartItems,
                    grandTotal,
                    subtotal,
                    tax,
                    delivery,
                    totalItems,
                  }}
                  className="w-full"
                >
                  <motion.button
                    className="w-full bg-red-600 text-white py-3 rounded-xl text-sm tracking-wide"
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    whileHover={{ scale: 1.06, backgroundColor: "#b91c1c" }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Proceed to Checkout
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
